import { authorizedRequest, HTTP_METHODS } from "utils/request";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { AuthorizedRequestBuilder } from "utils/sagaBuilder";
import * as CONSTANTS from "./constants";
import { generic } from "../../utils/action";
import { getPractice } from "../../utils/practiceStorage";
import {
  getPracticeMessagesError,
  getPracticeMessagesSuccess,
  getPracticeProductOrdersError,
  getPracticeProductOrdersSuccess,
} from "./actions";

// Individual exports for testing
export const getPlannedTimeSlotsSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_PLANNED_TIMESLOTS_SUCCESS,
  CONSTANTS.GET_PLANNED_TIMESLOTS_FAILED,
)
  .url(CONSTANTS.PLANNED_TIMESLOTS_URL)
  .callbackBuilder((action, result) => () =>
    action.callback && action.callback(result),
  )
  .build();

export const filterPatientSaga = new AuthorizedRequestBuilder(
  CONSTANTS.FILTER_PATIENTS_SUCCESS_ACTION,
  CONSTANTS.FILTER_PATIENTS_FAILED_ACTION,
)
  .url(CONSTANTS.FILTERED_PATIENTS_URL)
  .method(HTTP_METHODS.POST)
  .body(({ filterText }) => filterText)
  .build();

// TODO all dentist related items have to be moved to DentistOverviewPage!!
export const filterDentistsSaga = new AuthorizedRequestBuilder(
  CONSTANTS.FILTER_DENTIST_SUCCESS,
  CONSTANTS.FILTER_DENTIST_FAILED,
)
  .url(CONSTANTS.FILTERED_DENTIST_URL)
  .method(HTTP_METHODS.POST)
  .body(({ filterText }) => filterText)
  .build();

export const getEndProductTypesSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_END_PRODUCTS_SUCCESS,
  CONSTANTS.GET_END_PRODUCTS_FAILED,
)
  .url(CONSTANTS.END_PRODUCTS_URL)
  .build();

// get InsurersList
export const getInsurersSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_INSURERS_SUCCESS_ACTION,
  CONSTANTS.GET_INSURERS_FAILED_ACTION,
)
  .url(CONSTANTS.INSURER_URL)
  .build();

// get DentistsList
export const getDentistsSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_DENTISTS_SUCCESS_ACTION,
  CONSTANTS.GET_DENTISTS_FAILED_ACTION,
)
  .url(CONSTANTS.DENTISTS_URL)
  .build();

export function* createPatientSaga({ patient, callback, existing }) {
  try {
    let method = HTTP_METHODS.POST;
    if (existing) {
      method = HTTP_METHODS.PUT;
    }

    const patientResult = yield call(
      authorizedRequest,
      CONSTANTS.PATIENTS_URL(),
      {
        method,
        body: patient,
      },
    );

    yield put(
      generic(CONSTANTS.CREATE_PATIENT_SUCCESS_ACTION, {
        result: patientResult,
      }),
    );
    yield call(callback, patientResult);
  } catch (e) {
    yield put(
      generic(CONSTANTS.CREATE_PATIENT_FAILED_ACTION, {
        reason: e.response.data,
      }),
    );
  }
}

// to DentistOverview page
export function* createDentistSaga({ dentist, callback, existing }) {
  try {
    let method = HTTP_METHODS.POST;
    if (existing) {
      method = HTTP_METHODS.PUT;
    }

    const dentistResult = yield call(
      authorizedRequest,
      CONSTANTS.DENTISTS_URL(),
      {
        method,
        body: dentist,
      },
    );

    yield put(
      generic(CONSTANTS.CREATE_DENTIST_SUCCESS_ACTION, {
        result: dentistResult,
      }),
    );
    yield call(callback, dentistResult);
  } catch (e) {
    yield put(
      generic(CONSTANTS.CREATE_DENTIST_FAILED_ACTION, {
        reason: e.response.data,
      }),
    );
  }
}

export const getTreatmentsSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_TREATMENTS_SUCCESS_ACTION,
  CONSTANTS.GET_TREATMENTS_FAILED_ACTION,
)
  .url(CONSTANTS.FILTER_APPOINTMENTS_URL)
  .body({ filter: "" })
  .method(HTTP_METHODS.POST)
  .build();

export const getProductOrdersSaga = new AuthorizedRequestBuilder(
  CONSTANTS.GET_PRODUCT_ORDERS_SUCCESS_ACTION,
  CONSTANTS.GET_PRODUCT_ORDERS_FAILED_ACTION,
)
  .url(CONSTANTS.PRODUCT_ORDER_URL)
  .build();

/**
 * Delete dentist
 */
export const deleteDentistSaga = new AuthorizedRequestBuilder(
  CONSTANTS.DELETE_DENTIST_SUCCESS_ACTION,
  CONSTANTS.DELETE_DENTIST_FAILED_ACTION,
)
  .method(HTTP_METHODS.DELETE)
  .body(({ dentist }) => dentist)
  .url(CONSTANTS.DELETE_DENTIST_URL)
  .build();

// get Messages

export function* getMessages({ limit, offset, filter, status }) {
  try {
    const practiceId = getPractice().id;

    const result = yield call(
      authorizedRequest,
      `/practices/${practiceId}/messages/pagination/filter?limit=${limit}&offset=${offset}`,
      {
        method: HTTP_METHODS.POST,
        body: { filter, status },
      },
    );

    yield put(
      getPracticeMessagesSuccess({
        messages: result.list,
        statusOpenCount: result.statusOpenCount,
      }),
    );
  } catch (err) {
    yield put(getPracticeMessagesError(err));
  }
}

export function* getPracticeProductOrdersSaga() {
  try {
    const practice = getPractice();
    const response = yield authorizedRequest(
      `/practices/${practice.id}/productOrders`,
    );
    yield put(getPracticeProductOrdersSuccess(response));
  } catch (err) {
    yield put(getPracticeProductOrdersError(err.message));
  }
}

export default function* AppSaga() {
  yield all([
    takeEvery(CONSTANTS.GET_PLANNED_TIMESLOTS, getPlannedTimeSlotsSaga),
    takeEvery(CONSTANTS.CREATE_PATIENT_ACTION, createPatientSaga),
    takeEvery(CONSTANTS.GET_DENTISTS_ACTION, getDentistsSaga),
    takeEvery(CONSTANTS.GET_INSURERS_ACTION, getInsurersSaga),
    takeEvery(CONSTANTS.GET_TREATMENTS_ACTION, getTreatmentsSaga),
    takeEvery(CONSTANTS.CREATE_DENTIST_ACTION, createDentistSaga),
    takeEvery(CONSTANTS.FILTER_PATIENTS_ACTION, filterPatientSaga),
    takeEvery(CONSTANTS.FILTER_DENTIST, filterDentistsSaga),
    takeEvery(CONSTANTS.GET_END_PRODUCTS, getEndProductTypesSaga),
    takeEvery(CONSTANTS.DELETE_DENTIST_ACTION, deleteDentistSaga),
    takeLatest(CONSTANTS.GET_PRODUCT_ORDERS_ACTION, getProductOrdersSaga),
    takeLatest(CONSTANTS.GET_PRACTICE_MESSAGES, getMessages),
    takeLatest(
      CONSTANTS.GET_PRACTICE_PRODUCT_ORDERS,
      getPracticeProductOrdersSaga,
    ),
  ]);
}
